import { createSelector, createSlice } from "@reduxjs/toolkit";
import { logout } from "../../common/reducers/initContext";
import { dataHookFactory, initMapFactory, initMultiMapFactory, queryMapFactory, DefaultFailureAction, DefaultSuccessAction, dataHookWithCredentialsFactory, readonlyHookWithCredentialsFactory, compareArray } from "../../common/reducers/utilities";
import { aggregatingFetch } from "../../common/hooks/networkHook";
import { dateRangeOverlap, dateSameOrAfter } from "../../common/utils/dateUtils";
import { ensureArray, groupBy } from "../../common/utils/arrayUtilities";
import { ensureInteger } from "../../common/utils/generalUtils";
import { canMgrViewEvents } from "../event/eventMgrSlice";
import { markFetched, registerEntityBasedFetch } from "../../common/hooks/initHook";
import { complexTableQuery } from "../../common/utils/tableUtilities";
import { buildSearchQuery } from "../../utils/SearchScreenUtils";

const key = shift => ensureInteger(shift.id);

// shift render id is added as a computed field for purposes such as rendering as part of the data set in search screns, an update will never be triggered on this field so it will not be sent to the server
const prepareArray = data => ensureArray(data).map(shift => ({
    ...shift,
    renderid: shiftIdString(shift)
}))

export const shiftMgrSlice = createSlice({
    name: 'shifts',
    initialState: {
        data: {},
    },
    reducers: {
        set: (state, action) => {
            ensureArray(action.payload.ids).forEach(id => delete state.data[id]);
            prepareArray(action.payload.data).forEach(shift => state.data[key(shift)] = shift);
        },
        setReplicas: (state, action) => {
            const toRemove = new Set(Object.values(state.data).filter(shift => shift.replicationid && Object.hasOwn(action.payload, shift.replicationid)).map(s => key(s)));
            Object.values(action.payload).forEach(shifts => {
                prepareArray(shifts).forEach(shift => {
                    state.data[key(shift)] = shift;
                    toRemove.delete(key(shift));
                })
            })
            toRemove.forEach(id => delete state.data[id]);
        },
        addOrUpdate: (state, action) => {
            prepareArray(action.payload).forEach(s => state.data[key(s)] = s);
        },
        remove: (state, action) => {
            ensureArray(action.payload).forEach(s => delete state.data[key(s)]);
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => shiftMgrSlice.getInitialState());
    }
});

// #region network operations
const visualizeSuccess = (verb, action) => DefaultSuccessAction(verb, 'shift_one', action);
const visualizeFailure = verb => DefaultFailureAction(verb, 'shift_one');


const fetchShift = aggregatingFetch(
    id => network => {
        return network.get('/api/v1/shift/' + encodeURIComponent(id), data => shiftMgrSlice.actions.set({ data, ids: [id] }), visualizeFailure('get'))
    },
    ids => network => network.post('/api/v2/shifts', ids, data => shiftMgrSlice.actions.set({ data, ids: ensureArray(ids) }), visualizeFailure('get')),
    { primitiveKey: true }
)
const fetchShiftsInRange = (start, end) => network =>
    network.get(`/api/v2/shifts/${encodeURIComponent(start)}/${encodeURIComponent(end)}`, shiftMgrSlice.actions.addOrUpdate, visualizeFailure('get'));

const fetchSearchShifts = (search) => network => {
    const query = buildSearchQuery(search);
        let props = Object.keys(query);
        if (props.length === 0 || (props.length === 1 && query.status === '@')) {
            return [];
        }
    network.post('/api/v1/shiftSearch', query, shiftMgrSlice.actions.addOrUpdate, visualizeFailure('get'));
}

const fetchEventShifts = aggregatingFetch(
    eventid => network => network.post('/api/v2/eventShifts', [ eventid ], shiftMgrSlice.actions.addOrUpdate, visualizeFailure('get')),
    eventids => network => network.post('/api/v2/eventShifts', Array.from(eventids), shiftMgrSlice.actions.addOrUpdate, visualizeFailure('get'))
)

const fetchShiftReplicas = aggregatingFetch(
    id => network => network.post('/api/v2/shiftReplicas', [id], shiftMgrSlice.actions.setReplicas, visualizeFailure('get')),
    ids => network => network.post('/api/v2/shiftReplicas', Array.from(ids), shiftMgrSlice.actions.setReplicas, visualizeFailure('get')),
    { primitiveKey: true }
);

export const repeatShift = (network, shift) => network.post('/api/v1/shiftReplicate', shift, shiftMgrSlice.actions.addOrUpdate, visualizeFailure('repeat') )

export const createShift = (network, shift) => network.post('/api/v1/shift', shift, shiftMgrSlice.actions.addOrUpdate, visualizeFailure('create'));
export const updateShift = (network, delta) => network.put('/api/v1/shift', delta, shiftMgrSlice.actions.addOrUpdate, visualizeFailure('update'));
export const removeShift = (network, id, mode) => network.delete('/api/v2/shift/' + encodeURIComponent(id) + (mode ? '/' + mode : ''), shiftMgrSlice.actions.remove, visualizeFailure('remove'));
export const updateShiftReport = (network, shiftid, report) => network.post('/api/v1/shift/report', {shiftid, report}, visualizeSuccess('update'), visualizeFailure('update'))
// #endregion

// #region selectors
const selectShiftData = state => state.shifts.data;
const selectShift = id => createSelector([selectShiftData], shifts => shifts[id]);
const selectShifts = ids => createSelector([selectShiftData], shifts => {
    return ensureArray(ids).reduce((res, id) => {
        if (shifts[id]) {
            res.push(shifts[id]);
        }
        return res;
    }, []);
});
const selectEventShifts = eventid => createSelector([selectShiftData], shifts => Object.values(shifts).filter(s => s.eventid === eventid));
const selectEventsShifts = eventids => createSelector([selectShiftData], shifts => Object.values(shifts).filter(s => eventids.some(eid => eid == s.eventid)));


// sort is important for things like the RosterGrid which assume an order
const selectShiftsInRange = (start, end) => createSelector([selectShiftData], shifts => {
    let result = Object.values(shifts).filter(s => dateRangeOverlap(s, { start, end }));
    result.sort((a, b) => (new Date(a.start)).getTime() - (new Date(b.start)).getTime());
    return result;
});

const selectSearchShiftsInRange = search => createSelector([selectShiftData], shifts => {
    const { start, end, ...query } = search;
    const searchFields = Object.keys(query);

    if (searchFields.some(f => f !== 'templateid')) {
        throw new Error("Untested field(s) specified. Verify that each is handled correctly by complexTableQuery.");
    }
    
    let results = Object.values(shifts).filter(s => dateRangeOverlap(s, { start, end }));
    let ignoreFields = searchFields.filter(f => query[f] === undefined || query[f] === null || (Array.isArray(query[f]) && search[f].length === 0));
    if (searchFields.length === ignoreFields.length) {
        return results;
    }

    return complexTableQuery(results, query, { ignoreFields });
})

const selectShiftReplicaData = createSelector([selectShiftData], shifts => groupBy(Object.values(shifts).filter(s => s.replicationid), 'replicationid'));
const selectShiftReplicas = (id, replicationid, editMode) => createSelector([selectShiftReplicaData], replicas => {
    if (editMode === 'all') {
        return replicas[replicationid] ?? [];
    } else if (editMode === 'none') {
        let shift = replicas[replicationid]?.find(r => r.id === id);
        return  shift ? [shift] : [];
    } else if (editMode === 'future') {
        let baseShift = replicas[replicationid]?.find(r => r.id === id);
        if (!baseShift) {
            return [];
        }
        return replicas[replicationid].filter(e => dateSameOrAfter(baseShift, e));
    } else {
        return []; 
    }
});
// #endregion

const listen = {
    shiftAdminCreated: (shift, { dispatch, websocket }) => {
        markFetched(websocket, 'shift', shift.id);
        dispatch(shiftMgrSlice.actions.addOrUpdate(shift))
    },
    shiftAdminUpdated: (shift, { dispatch }) => dispatch(shiftMgrSlice.actions.addOrUpdate(shift)),
    shiftAdminRemoved: (shift, { dispatch }) => dispatch(shiftMgrSlice.actions.remove(shift)) 
}

// #region utilities
export const shiftIdString = shift => shift.eventid + '.' + ('000' + shift.sequence).slice(-3)
// #endregion

const cleanup = { root: shiftMgrSlice.name, all: state => Object.values(selectShiftData(state)), removeAll: shiftMgrSlice.actions.remove, key }
const options = {
    cleanup,
    condition: canMgrViewEvents
}

const optionsMultiple = { condition: canMgrViewEvents }
const optionsSingle = { condition: canMgrViewEvents, defaultValue: null}

const shiftInitByID = initMapFactory(fetchShift, listen, { name: 'shiftByID' });
registerEntityBasedFetch('shift', shiftInitByID);

const shiftInitByEventID = queryMapFactory(fetchEventShifts, 'id', shiftInitByID, listen);
registerEntityBasedFetch('event', shiftInitByEventID);

export const [useMgrShift, realizeMgrShift] = dataHookWithCredentialsFactory(selectShift, shiftInitByID, { ...options, defaultValue: null });
export const [useMgrShifts] = dataHookWithCredentialsFactory(selectShifts, initMultiMapFactory(shiftInitByID, listen), { ...options, comparator: compareArray });
export const [useMgrShiftsInRange, realizeMgrShiftsInRange] = dataHookWithCredentialsFactory(selectShiftsInRange, queryMapFactory(fetchShiftsInRange, 'id', shiftInitByID, listen), { ...options, comparator: compareArray });
export const [useMgrEventShifts, realizeMgrEventShifts] = dataHookWithCredentialsFactory(selectEventShifts, shiftInitByEventID, { ...options, comparator: compareArray });
export const [useMgrEventsShifts, realizeMgrEventsShifts] = dataHookWithCredentialsFactory(selectEventsShifts, initMultiMapFactory(shiftInitByEventID, listen), { ...options, comparator: compareArray });

export const [useMgrShiftSearchInRange] = dataHookWithCredentialsFactory(selectSearchShiftsInRange, queryMapFactory(fetchSearchShifts, 'id', shiftInitByID, listen), { ...options, comparator: compareArray });

export const [useReadMgrShift] = readonlyHookWithCredentialsFactory(selectShift, id => shiftInitByID, optionsSingle);
export const [useReadMgrShifts] = readonlyHookWithCredentialsFactory(selectShifts, ids => shiftInitByID, optionsMultiple);

// WARNING: do not replace the use of this constant with the function invocation - doing so will break the singleton pattern required by factories
// consult on any changes to the below since we are doing an explicit argument drop on the call chain
const replicaInitByID = queryMapFactory(fetchShiftReplicas, 'id', shiftInitByID, listen);
export const [useShiftReplicas] = dataHookFactory(selectShiftReplicas, id => replicaInitByID(id), { cleanup })

const reducer = { name: shiftMgrSlice.name, reducer: shiftMgrSlice.reducer }
export default reducer;